import { createSelector } from 'reselect';
import { get, last } from 'lodash';
import { surveyStatusEnum } from 'containers/SurveysPage/constants';
import {
  makeSelectAccountPerks,
  makeSelectIsPollfishAdmin,
} from 'containers/User/shared-selectors';
import { makeSelectUserEditLiveQuestionnaire } from 'containers/User/selectors';

import { isSurveyConfigManagedTargeting } from 'containers/Permissions/helpers';
import { VARIABLES } from 'containers/LinkSettings/constants';
import {
  selectSurveyDomain,
  makeSelectIsDistributionLink,
  makeSelectIsPeeq,
  makeSelectIsThirdParty,
  makeSelectIsSurveyManagedTargeting,
} from './shared-selectors';

import {
  SURVEY_TYPE_REGULAR,
  SURVEY_TYPE_THIRD_PARTY,
  SURVEY_TYPE_INTERNAL,
  SURVEY_TYPE_DISTRIBUTION_LINK,
  SURVEY_TYPE_PEEQ,
} from './constants';

const makeSelectSurveyDetails = createSelector(selectSurveyDomain, (substate) =>
  get(substate, 'surveyDetails', {}),
);

const makeSelectSurveyId = createSelector(selectSurveyDomain, (substate) =>
  get(substate, 'surveyDetails.id'),
);

const makeSelectSurveyHash = createSelector(selectSurveyDomain, (substate) =>
  get(substate, 'surveyDetails.hash'),
);

const makeSelectSurveyStatusName = createSelector(
  selectSurveyDomain,
  (substate) => get(substate, 'surveyDetails.statusName', ''),
);

const makeSelectSurveyHasFailedPayment = createSelector(
  selectSurveyDomain,
  (substate) => get(substate, 'surveyDetails.isFailedPayment', false),
);

const makeSelectSurveyType = createSelector(selectSurveyDomain, (substate) => {
  const surveyDetails = get(substate, 'surveyDetails');
  if (!surveyDetails) {
    return undefined;
  }
  if (surveyDetails.isInternal) {
    return SURVEY_TYPE_INTERNAL;
  }
  if (surveyDetails.isThirdParty) {
    return SURVEY_TYPE_THIRD_PARTY;
  }
  if (surveyDetails.isDistributionLink) {
    return SURVEY_TYPE_DISTRIBUTION_LINK;
  }
  if (surveyDetails.isPeeq) {
    return SURVEY_TYPE_PEEQ;
  }

  return SURVEY_TYPE_REGULAR;
});

const makeSelectLinkSettingsMaxResponses = (state) => {
  const linkSettings = get(state, 'linkSettings', {});
  return get(linkSettings, VARIABLES.MAX_RESPONSES);
};

const makeSelectLinkSettingsFetched = (state) =>
  Boolean(get(state, 'linkSettings.fetchedSettings'));

const makeSelectSurveyTotalRemainingCompletes = createSelector(
  selectSurveyDomain,
  makeSelectIsDistributionLink,
  makeSelectLinkSettingsMaxResponses,
  (substate, isDistributionLink, distributionLinkMaxResponses) => {
    const { completes: surveyCompletes = 0, completed = 0 } = get(
      substate,
      'surveyDetails',
      {},
    );
    if (isDistributionLink) {
      return distributionLinkMaxResponses - completed;
    }
    return surveyCompletes - completed;
  },
);

const makeSelectSurveyPreviousRemainingCompletes = createSelector(
  selectSurveyDomain,
  (substate) => {
    const { previousCompletes = 0, completed = 0 } = get(
      substate,
      'surveyDetails',
      {},
    );
    return previousCompletes - completed;
  },
);

const makeSelectSurveyCompletes = createSelector(
  selectSurveyDomain,
  (substate) => {
    const { completes = 0 } = get(substate, 'surveyDetails', {});
    return completes;
  },
);

const makeSelectSurveyCompleted = createSelector(
  selectSurveyDomain,
  (substate) => {
    const { completed = 0 } = get(substate, 'surveyDetails', {});
    return completed;
  },
);

const makeSurveyIsUnderEdit = createSelector(selectSurveyDomain, (substate) => {
  const statusName = get(substate, 'surveyDetails.statusName', '');
  return statusName === surveyStatusEnum.UNDER_EDIT;
});

const makeSurveyIsPendingToStart = createSelector(
  selectSurveyDomain,
  (substate) => {
    const statusName = get(substate, 'surveyDetails.statusName', '');
    return statusName === surveyStatusEnum.PENDING_TO_START;
  },
);

const makeSurveyIsRunning = createSelector(selectSurveyDomain, (substate) => {
  const statusName = get(substate, 'surveyDetails.statusName', '');
  return statusName === surveyStatusEnum.RUNNING;
});

const makeSurveyIsDraft = createSelector(selectSurveyDomain, (substate) => {
  const statusName = get(substate, 'surveyDetails.statusName', '');
  return statusName === surveyStatusEnum.DRAFT;
});

const makeSurveyIsUnderApproval = createSelector(
  selectSurveyDomain,
  (substate) => {
    const statusName = get(substate, 'surveyDetails.statusName', '');
    return statusName === surveyStatusEnum.UNDER_APPROVAL;
  },
);

const makeRequiresManualSaveQuestionnaire = createSelector(
  selectSurveyDomain,
  makeSelectIsPollfishAdmin,
  makeSelectIsPeeq,
  makeSelectIsDistributionLink,
  makeSelectUserEditLiveQuestionnaire,
  (
    surveyState,
    isPollfishAdmin,
    isSurveyPeeq,
    isSurveyDistributionLink,
    hasPerkEditLiveQuestionnaire,
  ) => {
    const statusName = get(surveyState, 'surveyDetails.statusName', '');
    const hasEditLiveQuestionnaireInUnderEdit =
      statusName === surveyStatusEnum.UNDER_EDIT &&
      hasPerkEditLiveQuestionnaire;

    if (isSurveyPeeq) {
      const isManagedTargeting = isSurveyConfigManagedTargeting(surveyState);

      if (isManagedTargeting) {
        return (
          statusName !== surveyStatusEnum.DRAFT &&
          statusName !== surveyStatusEnum.APPROVED &&
          !hasEditLiveQuestionnaireInUnderEdit
        );
      }

      if (
        !isManagedTargeting &&
        statusName !== surveyStatusEnum.DRAFT &&
        statusName !== surveyStatusEnum.UNDER_APPROVAL &&
        hasEditLiveQuestionnaireInUnderEdit
      ) {
        return false;
      }

      if (
        !isManagedTargeting &&
        statusName !== surveyStatusEnum.DRAFT &&
        statusName !== surveyStatusEnum.UNDER_APPROVAL &&
        statusName !== surveyStatusEnum.UNDER_EDIT &&
        statusName !== surveyStatusEnum.APPROVED
      ) {
        return isPollfishAdmin;
      }

      return false;
    }

    if (isSurveyDistributionLink) {
      if (
        statusName !== surveyStatusEnum.DRAFT &&
        statusName !== surveyStatusEnum.UNDER_APPROVAL &&
        hasEditLiveQuestionnaireInUnderEdit
      ) {
        return false;
      }

      if (
        statusName !== surveyStatusEnum.DRAFT &&
        statusName !== surveyStatusEnum.UNDER_EDIT
      ) {
        return isPollfishAdmin;
      }

      return false;
    }

    if (statusName) {
      if (statusName === surveyStatusEnum.APPROVED) {
        return isPollfishAdmin;
      }

      return (
        statusName !== surveyStatusEnum.UNDER_APPROVAL &&
        statusName !== surveyStatusEnum.DRAFT
      );
    }
    return false;
  },
);

const makeSelectSurveyFolder = createSelector(
  selectSurveyDomain,
  (substate) => {
    const folders = get(substate, 'surveyDetails.folders', []);
    const folder = last(folders);
    if (folder) {
      return folder.id;
    }
    return '';
  },
);

const makeSelectTermsAndConditionsSaving = createSelector(
  selectSurveyDomain,
  (substate) => Boolean(substate.isSavingTerms),
);
const makeSelectTermsAndConditionsIsEnabled = createSelector(
  makeSelectSurveyDetails,
  (surveyDetails) => Boolean(get(surveyDetails, 'terms.isEnabled')),
);
const makeSelectTermsAndConditions = createSelector(
  makeSelectSurveyDetails,
  (surveyDetails) => get(surveyDetails, 'terms', {}),
);

const makeSelectSurveyPaymentType = createSelector(
  selectSurveyDomain,
  (substate) => get(substate, 'surveyDetails.paymentType'),
);

const makeSelectSurveyCardId = createSelector(selectSurveyDomain, (substate) =>
  get(substate, 'surveyDetails.cardId'),
);

/**
 * Default selector used by Survey
 */

const makeSelectSurvey = createSelector(
  selectSurveyDomain,
  (substate) => substate,
);

const makeSelectSurveyIsLoading = createSelector(
  selectSurveyDomain,
  (substate) => substate.isLoading,
);

const makeSelectSurveyHasFetched = createSelector(
  selectSurveyDomain,
  (substate) => substate.hasFetched || false,
);

const makeSelectIsSurveySaving = createSelector(
  selectSurveyDomain,
  (substate) => substate.isSaving,
);

const makeSelectSurveyConfig = createSelector(
  selectSurveyDomain,
  (substate) => get(substate, 'surveyConfig') || {},
);

const makeSelectShouldSendDisqualificationRulesOnSave = createSelector(
  makeSelectSurveyConfig,
  (surveyConfig) => get(surveyConfig, 'supportsFraudChecks', true),
);

const makeSelectHasPermissionForDisqualificationRules = createSelector(
  makeSelectAccountPerks,
  makeSelectSurveyConfig,
  makeSelectIsSurveyManagedTargeting,
  (perkMetadata, surveyConfig, isSurveyManagedTargeting) => {
    const hasUserPermission = get(
      perkMetadata,
      'disqualificationRulesPerk',
      false,
    );

    const supportsFraudChecks = get(surveyConfig, 'supportsFraudChecks', true);

    return (
      hasUserPermission && supportsFraudChecks && !isSurveyManagedTargeting
    );
  },
);

const makeSelectSurveyLanguage = createSelector(
  selectSurveyDomain,
  (substate) => get(substate, 'surveyDetails.lang'),
);

const makeSelectIsSurveyNameSaving = createSelector(
  selectSurveyDomain,
  (substate) => substate.isSavingName,
);

const makeSelectSurveyIsFetching = createSelector(
  selectSurveyDomain,
  (substate) => substate.isFetching || false,
);

const makeSelectHasDifferencesInCompletes = createSelector(
  selectSurveyDomain,
  (substate) => get(substate, 'surveyDetails.hasDifferencesInCompletes', false),
);

const makeSelectSurveyInformationForQuestionnaire = createSelector(
  makeSurveyIsUnderEdit,
  makeSurveyIsDraft,
  makeSelectSurveyCompletes,
  makeSelectSurveyCompleted,
  (isUnderEdit, isDraft, completes, completed) => ({
    isUnderEdit,
    isDraft,
    completes,
    completed,
    remainingCompletes: completes - completed,
  }),
);

const makeSelectSurveyPreviousStatusName = createSelector(
  selectSurveyDomain,
  (substate) => get(substate, 'surveyDetails.previousStatusName'),
);

const makeSelectIsSurveyScheduled = createSelector(
  selectSurveyDomain,
  (substate) => get(substate, 'surveyDetails.isScheduled', false),
);

const makeSelectIsSurveyRecurring = createSelector(
  selectSurveyDomain,
  (substate) => get(substate, 'surveyDetails.isRecurring', false),
);

export default makeSelectSurvey;
export {
  makeSelectSurvey,
  makeSelectSurveyId,
  makeSelectSurveyHash,
  makeSelectSurveyDetails,
  makeSelectSurveyStatusName,
  makeSelectSurveyType,
  makeSelectSurveyFolder,
  selectSurveyDomain,
  makeSelectSurveyPreviousRemainingCompletes,
  makeSelectSurveyTotalRemainingCompletes,
  makeSelectSurveyCompletes,
  makeSurveyIsUnderEdit,
  makeSurveyIsDraft,
  makeSurveyIsUnderApproval,
  makeSurveyIsPendingToStart,
  makeSurveyIsRunning,
  makeRequiresManualSaveQuestionnaire,
  makeSelectTermsAndConditions,
  makeSelectTermsAndConditionsSaving,
  makeSelectTermsAndConditionsIsEnabled,
  makeSelectSurveyPaymentType,
  makeSelectSurveyCardId,
  makeSelectSurveyIsLoading,
  makeSelectSurveyHasFetched,
  makeSelectIsSurveySaving,
  makeSelectIsDistributionLink,
  makeSelectSurveyCompleted,
  makeSelectHasPermissionForDisqualificationRules,
  makeSelectShouldSendDisqualificationRulesOnSave,
  makeSelectSurveyLanguage,
  makeSelectIsSurveyNameSaving,
  makeSelectIsPeeq,
  makeSelectIsThirdParty,
  makeSelectSurveyConfig,
  makeSelectSurveyIsFetching,
  makeSelectHasDifferencesInCompletes,
  makeSelectSurveyInformationForQuestionnaire,
  makeSelectSurveyPreviousStatusName,
  makeSelectIsSurveyScheduled,
  makeSelectIsSurveyRecurring,
  makeSelectSurveyHasFailedPayment,
  makeSelectLinkSettingsMaxResponses,
  makeSelectLinkSettingsFetched,
};
